import { Doc, generateToDocFn } from '@dadohr/shared/domainTypes/document';
import {
  LessonlyContentOption,
  LessonlyContentType,
  LessonlyIntegrationConfig
} from '@dadohr/shared/domainTypes/integrations/lessonly';
import { DEMO_ORG_IDS } from '@dadohr/shared/domainTypes/org';
import { CF, FS } from '@dadohr/shared/versions';
import { getAppUrl } from '../../../../../../admin/services/routes';
import {
  store,
  useMappedLoadingValue
} from '../../../../../../services/firebase';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../../../../../../services/firecache/documentListener';
import { now } from '../../../../../../services/time';

export const toLessonlyConfigDoc = generateToDocFn<LessonlyIntegrationConfig>();

const COLLECTION = FS.lessonlyIntegrationConfig;
const collection = () => store().collection(COLLECTION);
export const lessonlyConfigStore = createDocumentListenerGetter(
  (orgId) => collection().doc(orgId),
  toLessonlyConfigDoc
);

export const useLessonlyConfig = (orgId: string, userId: string) => {
  return useMappedLoadingValue(
    useDocumentListener(lessonlyConfigStore(orgId)),
    (d) => {
      if (!d) {
        const n = now();
        const newConfig: Doc<LessonlyIntegrationConfig> = {
          id: orgId,
          collection: COLLECTION,
          data: {
            orgId,
            active: false,
            createdAt: n,
            createdBy: userId,
            editedAt: n,
            editedBy: userId,

            subdomain: '',
            apiKey: ''
          }
        };
        return newConfig;
      }
      return d;
    }
  );
};

export const removeLessonlyConfig = (orgId: string) => {
  return collection().doc(orgId).delete();
};

export const getLessonlyWebhookUrl = () => `${getAppUrl()}api/lessonly/webhook`;

export const setupLessonlyIntegration = (
  orgId: string,
  args: { subdomain: string; apiKey: string }
) =>
  callFirebaseFunction<{ success: true }>(CF.lessonly.verifyCredentialsAndStore, {
    orgId,
    ...args
  });

// The setup callable rejects bad credentials with HttpsError('invalid-argument',
// 'INVALID_CREDENTIALS') so the dialog can show it inline.
export const isInvalidLessonlyCredentialsError = (err: unknown) =>
  (err as { message?: unknown } | null)?.message === 'INVALID_CREDENTIALS';

// Non-archived lessons and paths (the backend filters archived content).
export const getLessonlyContentOptions = async (
  orgId: string
): Promise<LessonlyContentOption[]> => {
  if (DEMO_ORG_IDS.includes(orgId)) {
    return [
      { id: 123, title: 'Security Training', type: LessonlyContentType.LESSON },
      { id: 124, title: 'Privacy Training', type: LessonlyContentType.LESSON },
      { id: 125, title: 'Use cases of our product', type: LessonlyContentType.PATH }
    ];
  }
  return callFirebaseFunction<LessonlyContentOption[]>(
    CF.lessonly.getContentOptions,
    { orgId }
  );
};
